import { SagaIterator } from 'redux-saga';
import { call, delay, put, race, select, take, takeLatest } from 'redux-saga/effects';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { PS20Logger } from '@ps20-frontend/logging';
import { App, TierartEnum, TkvV2Vorbelegungdaten } from '@ps20-frontend/frontend-models';
import {
  BeibehaltungProduktRequestedPayload,
  determineTkvV2Produkttabelle,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  MasterProduktData,
  ProduktRequested,
  ProdukttabelleEnum
} from '@ps20-frontend/shop-integration-redux';
import { TaasTarifrisikoschluesselTKV_V2, Tierart } from '@ps20/bff-models';
import { AnyAction } from 'redux';
import { TkvAction, TkvActions } from '../tkvActions';
import { CoverageType } from '../models/vertragsoptionen.model';
import { IAppState } from '../appState';
import { selectTarifWechselOptionSelected } from '../selectors/produkt.selector';

export const PRODUKT_RECEIVED_TIMEOUT_MS = 10_000;

export function* watchProductRelevantDataChanged() {
  yield takeLatest(TkvActions.ANIMAL_TYPE_CHANGED, safeSaga(handleAnimalTypeChanged, App.TKV_V2));
  yield takeLatest(
    TkvActions.COVERAGE_TYPE_CHANGED,
    safeSaga(handleCoverageTypeChanged, App.TKV_V2),
  );
  yield takeLatest(
    GlobalActions.BEIBEHALTUNG_PRODUKT_REQUESTED,
    safeSaga(handleBeibehaltungProduktRequested, App.TKV_V2),
  );
}

export function* handleAnimalTypeChanged(action: TkvAction<TierartEnum>) {
  yield* loadAndUpdateProduct(action, action.payload);
}

export function* handleCoverageTypeChanged(action: TkvAction<CoverageType>) {
  const state: IAppState = yield select();
  yield* loadAndUpdateProduct(action, state.versicherungsnehmer.animalType, action.payload);
}

export function* handleBeibehaltungProduktRequested(
  action: GlobalAction<BeibehaltungProduktRequestedPayload>,
) {
  const { vorbelegungdaten } = action.payload;
  const state: IAppState = yield select();
  const isTkvApp = vorbelegungdaten.forApp === App.TKV_V2 || vorbelegungdaten.forApp === App.TKV;
  if (!isTkvApp || action.payload.angebotsId !== state.angebotsId) {
    return;
  }
  const tkvActions = new TkvActions();

  const tkvV2Vorbelegung = vorbelegungdaten as TkvV2Vorbelegungdaten;
  const animalType = mapTierartBffToFrontend(tkvV2Vorbelegung.tierart);
  if (animalType) {
    yield put(tkvActions.animalTypePrefilled(animalType));
    const coverageType = determineCoverageTypeFromVorbelegung(tkvV2Vorbelegung);
    PS20Logger.info(
      'TKV_V2.Beibehaltung',
      `angebotsId=${state.angebotsId} tierart=${tkvV2Vorbelegung.tierart} animalType=${animalType} coverageType=${coverageType} produkttabelle=${determineProdukttabelle(animalType, coverageType)}`,
      App.TKV_V2,
    );
    yield* loadAndUpdateProduct(action, animalType, coverageType);
    PS20Logger.info(
      'TKV_V2.Beibehaltung.produktLoaded',
      `angebotsId=${state.angebotsId} animalType=${animalType} coverageType=${coverageType}`,
      App.TKV_V2,
    );
  }
}

export function buildProduktReceivedMatcher(
  appId: App,
  angebotsId: string,
  expected: ProdukttabelleEnum,
) {
  return function matcher(a: AnyAction): boolean {
    if (a.type !== GlobalActions.PRODUKT_RECEIVED) {
      return false;
    }
    const payload = a.payload as MasterProduktData | undefined;
    return (
      payload?.appId === appId &&
      payload?.angebotsId === angebotsId &&
      payload?.produkttabelle === expected
    );
  };
}

export function* loadAndUpdateProduct(
  action: AnyAction,
  animalType: TierartEnum,
  coverageType?: CoverageType,
): SagaIterator {
  const globalPut = GlobalPut.getInstance();
  const state = (yield select()) as IAppState;
  const expected = determineProdukttabelle(animalType, coverageType);
  const tkvActions = new TkvActions();

  yield put(tkvActions.expectedProdukttabelleSet(expected));
  yield put(tkvActions.productLoadingInProgress(true));

  try {
    yield call(globalPut.put, productLoadingActions(state, animalType, coverageType));

    const matcher = buildProduktReceivedMatcher(App.TKV_V2, state.angebotsId, expected);
    const raceResult = (yield race({
      produkt: take(matcher),
      timeout: delay(PRODUKT_RECEIVED_TIMEOUT_MS),
    })) as { produkt?: AnyAction; timeout?: true };
    const { timeout } = raceResult;

    if (timeout) {
      PS20Logger.error(
        App.TKV_V2,
        `TKV_V2.loadAndUpdateProduct.timeout expected=${expected} angebotsId=${state.angebotsId}`,
        'PRODUKT_RECEIVED timeout',
      );
      return;
    }

    yield put(tkvActions.tarifwechselIncluded(selectTarifWechselOptionSelected(state)));
    yield put(tkvActions.productLoadingInProgress(false));
    if (!callerTriggersTarifierung(action)) {
      yield put(tkvActions.preiseRequested());
    }
  } finally {
    yield put(tkvActions.productLoadingInProgress(false));
    yield put(tkvActions.expectedProdukttabelleCleared());
  }
}

export function callerTriggersTarifierung(action: AnyAction): boolean {
  return (
    action.type === TkvActions.ANIMAL_TYPE_CHANGED ||
    action.type === GlobalActions.BEIBEHALTUNG_PRODUKT_REQUESTED ||
    action.tgvManaged === true
  );
}

export function determineProdukttabelle(
  animalType: TierartEnum,
  coverageType?: CoverageType,
): ProdukttabelleEnum {
  return determineTkvV2Produkttabelle(animalType, coverageType === CoverageType.VOLLSCHUTZ);
}

export function productLoadingActions(
  state: IAppState,
  animalType: TierartEnum,
  coverageType?: CoverageType,
  catalogsOnly?: boolean,
  catalogsForGroup?: boolean,
): GlobalAction<ProduktRequested> {
  const produkttabelle = determineProdukttabelle(animalType, coverageType);

  return new GlobalActions().produktRequested({
    appId: App.TKV_V2,
    angebotsId: state.angebotsId,
    produkttabelle,
    catalogsOnly,
    ...(catalogsForGroup ? { catalogsForGroup } : {}),
  });
}


export function mapTierartBffToFrontend(tierart: Tierart): TierartEnum | null {
  switch (tierart) {
    case Tierart.VRHUND:
      return TierartEnum.HUND;
    case Tierart.VRKATZE:
      return TierartEnum.KATZE;
    case Tierart.VRPFERD:
      return TierartEnum.PFERD;
    default:
      return null;
  }
}


export function determineCoverageTypeFromVorbelegung(
  vorbelegungdaten: TkvV2Vorbelegungdaten,
): CoverageType {
  const hasHeilbehandlungsBaustein = vorbelegungdaten.vorvertragBausteinList?.some(
    elem => elem.taasTarifrisikoschluessel === TaasTarifrisikoschluesselTKV_V2.HB,
  );
  return hasHeilbehandlungsBaustein ? CoverageType.VOLLSCHUTZ : CoverageType.OPSCHUTZ;
}


export function determineProdukttabelleFromVorbelegung(
  vorbelegungdaten: TkvV2Vorbelegungdaten,
): ProdukttabelleEnum | null {
  const animalType = mapTierartBffToFrontend(vorbelegungdaten.tierart);
  if (!animalType) {
    return null;
  }

  const coverageType = determineCoverageTypeFromVorbelegung(vorbelegungdaten);
  return determineProdukttabelle(animalType, coverageType);
}
